import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormAgendamentoConfirmacao } from './Confirmacao';
import { AgendaFormHeader } from './Header';
import { SelectHorario } from './SelectHorario';
import { SelectAtendente } from './SelectAtendente';
import { SelectServico } from './SelectServico';
import { FormHorarioClienteProvider, useFormHorarioClienteContext } from './Context';
import PlanoForm from './plano/Plano';
import { PlanoFormProvider } from './plano/Context';
import { BookingBar, capitalize, formatDuration } from '../../booking/BookingParts';
import { convertMinutesToHHMM, formatToReal } from '../../../../components/utils';
import { isPeriodoValido } from '../../../recorrencias/helpers';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';
import { useAppContext } from '../../../../contexts/AppContext';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import { Modal } from '../../../../components/Modal';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { filterMenusWithPlan } from '../../../../routes';
import { MENU_WIDTHS } from '../../../../dashboard/menu/Menu';
import { usePlanAccess } from '../../../../plans/usePlanAccess';
import './Form.css';

export const AgendaForm: React.FC<{
    doc?: any;
    onDone?: (doc: any) => void;
    redirectOnCreate?: boolean;
    embedded?: boolean;
    forceFloatingButton?: boolean;
    lockServico?: boolean;
    lockCliente?: boolean;
    lockAtendente?: boolean;
    waitlistEntryId?: string;
    recurrenceMode?: boolean;
    // Abre em tela cheia sobre a pagina (celular), cobrindo o cabecalho do app.
    mobileSheet?: boolean;
}> = ({
    doc,
    onDone,
    redirectOnCreate = true,
    embedded = false,
    forceFloatingButton = false,
    lockServico = false,
    lockCliente = false,
    lockAtendente = false,
    waitlistEntryId,
    recurrenceMode = false,
    mobileSheet = false,
}) => {
    const [modo, setModo] = useState<'rapido' | 'plano'>('rapido');
    const content = (
        <div className="sf-appointment-form relative w-full">
            {modo === 'rapido' ? (
                <Form
                    embedded={embedded}
                    forceFloatingButton={forceFloatingButton}
                    mobileSheet={mobileSheet}
                />
            ) : <PlanoFormProvider>
                <PlanoForm />
            </PlanoFormProvider>}
        </div>
    );

    return (
        <FormHorarioClienteProvider
            doc={doc}
            callback={onDone}
            redirectOnCreate={redirectOnCreate}
            lockServico={lockServico}
            lockCliente={lockCliente}
            lockAtendente={lockAtendente}
            waitlistEntryId={waitlistEntryId}
            recurrenceMode={recurrenceMode}
        >
            {mobileSheet ? <MobileBookingSheet>{content}</MobileBookingSheet> : content}
        </FormHorarioClienteProvider>
    );
};

const MobileBookingSheet: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { clear } = useParamsContext();
    const { canStepBack, stepBack, loading } = useFormHorarioClienteContext();

    // Voltar recua uma etapa antes de sair, para nao perder o que ja foi escolhido.
    const goBack = () => {
        if (loading) return;
        if (canStepBack) stepBack();
        else clear();
    };

    return (
        <Modal title="Novo agendamento" onClose={goBack} panelClassName="sf-page-sheet">
            {children}
        </Modal>
    );
};

const Form: React.FC<{
    embedded?: boolean;
    forceFloatingButton?: boolean;
    mobileSheet?: boolean;
}> = ({
    embedded = false,
    forceFloatingButton = false,
    mobileSheet = false,
}) => {
    const {
        openConfirm,
        setOpenConfirm,
        atendente,
        servico,
        servicosExtras,
        showHorarios,
        setShowHorarios,
        horario,
        data,
        valor,
        duration,
        clientId,
        loading,
        submitForm,
        recurrenceMode,
        recorrencia,
        doc,
    } = useFormHorarioClienteContext();
    const hasAutoOpenedHorarios = useRef(false);
    const isMobile = useMediaQuery({ maxWidth: 800 });
    const { shortMenu, subMenu } = useAppContext();
    const { user } = useAuthContext();
    const { menuShowEntry, resolvedPlanKey, billingStatus } = usePlanAccess();

    const showMenu = useMemo(() => {
        if (!user) return false;
        return filterMenusWithPlan(user, menuShowEntry).length > 1;
    }, [user, resolvedPlanKey, billingStatus, menuShowEntry]);

    // Dentro de modal ou painel (fila, edicao, faixa da agenda) a barra fica no proprio painel.
    const inlineBar = embedded || forceFloatingButton;

    const contentOffset = useMemo(() => {
        if (inlineBar || isMobile || !showMenu) return 0;
        const menuWidth = shortMenu ? MENU_WIDTHS.collapsed : MENU_WIDTHS.expanded;
        const subMenuWidth = subMenu ? MENU_WIDTHS.subMenu + MENU_WIDTHS.gap : 0;
        return menuWidth + subMenuWidth + MENU_WIDTHS.offset;
    }, [inlineBar, isMobile, showMenu, shortMenu, subMenu]);

    useEffect(() => {
        if (!atendente?.uid || !servico?._id) {
            setShowHorarios(false);
        }
    }, [atendente?.uid, servico?._id, setShowHorarios]);

    useEffect(() => {
        hasAutoOpenedHorarios.current = false;
    }, [doc?._id]);

    useEffect(() => {
        if (!doc?._id || !servico?._id || hasAutoOpenedHorarios.current) return;
        setShowHorarios(true);
        hasAutoOpenedHorarios.current = true;
    }, [doc?._id, servico?._id, setShowHorarios]);

    const step = openConfirm ? 'confirm' : showHorarios ? 'horario' : atendente?.uid ? 'servico' : 'atendente';
    const serviceCount = 1 + servicosExtras.length;
    const priceLabel = Number(valor) > 0 ? `R$ ${formatToReal(Number(valor))}` : '';
    const recorrenciaInvalida = recurrenceMode && !doc?._id && !isPeriodoValido(recorrencia.inicio, recorrencia.fim);
    const confirmLabel = doc?._id ? 'Salvar' : recurrenceMode ? 'Criar recorrência' : 'Confirmar';

    const bar =
        step === 'servico' && servico?._id
            ? {
                  title: priceLabel || `${serviceCount} ${serviceCount === 1 ? 'serviço' : 'serviços'}`,
                  detail: `${serviceCount} ${serviceCount === 1 ? 'serviço' : 'serviços'}${duration ? ` · ${formatDuration(duration)}` : ''}`,
                  action: 'Escolher horário',
                  onClick: () => setShowHorarios(true),
              }
            : step === 'horario' && !!horario
              ? {
                    title: `${capitalize(moment(data).format('ddd, DD/MM'))} às ${convertMinutesToHHMM(horario)}`,
                    detail: priceLabel,
                    action: 'Continuar',
                    disabled: loading || recorrenciaInvalida,
                    onClick: () => setOpenConfirm(true),
                }
              : step === 'confirm'
                ? {
                      title: priceLabel || 'Sem valor',
                      detail: clientId ? 'Total' : 'Selecione o cliente para continuar',
                      action: confirmLabel,
                      disabled: loading || !clientId,
                      onClick: submitForm,
                  }
                : null;

    return (
        <div className={`mx-auto w-full max-w-2xl ${bar && !inlineBar ? 'pb-28' : 'pb-4'}`}>
            <AgendaFormHeader step={step} hideBackOnMobile={mobileSheet} />
            <SelectAtendente visible={step === 'atendente'} />
            {!!atendente?.uid && <SelectServico visible={step === 'servico'} />}
            {/* Fica montado durante a revisao, para nao perder dia e horario ao voltar. */}
            {showHorarios && <div className={openConfirm ? 'hidden' : ''}><SelectHorario /></div>}
            {step === 'confirm' && <FormAgendamentoConfirmacao />}
            {bar && <BookingBar {...bar} busy={loading && step === 'confirm'} offset={contentOffset} inline={inlineBar} />}
        </div>
    );
};
